/*
 * ALACRAN — La escena del alacrán, dentro de la página.
 * El mismo alacrán de la entrada camina sobre la línea de horizonte de la portada
 * (o de la antesala), se recoge, ataca, y la luz de su aguijón baja a la línea:
 * ese punto encendido es el que se queda en la página. Luego aparece el resto.
 * Marcado: <canvas data-escena> dentro de la sección, y un .horizonte con .horizonte__luz.
 * Se ve una vez por visita; con "reducir movimiento" la luz aparece sin escena.
 */
(function () {
  'use strict';

  var html = document.documentElement;
  var KEY = 'alacran-escena-vista';

  function terminar(canvas) {
    html.classList.remove('escena-activa');
    html.classList.add('escena-encendida');
    try { sessionStorage.setItem(KEY, '1'); } catch (e) { /* sin almacenamiento */ }
    if (canvas) canvas.classList.add('is-apagada');
  }

  function iniciar() {
    var canvas = document.querySelector('[data-escena]');
    if (!html.classList.contains('escena-activa')) { html.classList.add('escena-encendida'); return; }
    if (!canvas || !canvas.getContext) { terminar(canvas); return; }
    var seccion = canvas.parentElement;
    var linea = seccion.querySelector('.horizonte');
    var luz = seccion.querySelector('.horizonte__luz');
    if (!linea || !luz) { terminar(canvas); return; }
    var ctx = canvas.getContext('2d');
    if (!ctx) { terminar(canvas); return; }

    var W = 0, H = 0, dpr = 1, S = 1;
    var L = { x0: 0, x1: 0, y: 0, cx: 0 };

    function medir() {
      var base = window.devicePixelRatio || 1;
      dpr = Math.min(2, base < 2 ? base * 2 : base);
      var r = seccion.getBoundingClientRect();
      W = r.width; H = r.height;
      canvas.width = Math.round(W * dpr); canvas.height = Math.round(H * dpr);
      canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
      var lr = linea.getBoundingClientRect(), dr = luz.getBoundingClientRect();
      L.x0 = lr.left - r.left; L.x1 = lr.right - r.left; L.y = lr.top - r.top + 0.5;
      L.cx = dr.left + dr.width / 2 - r.left;
      S = Math.max(1.1, Math.min(2.3, (L.x1 - L.x0) / 300));
    }
    medir();
    window.addEventListener('resize', medir);

    function clamp(v, a, b) { return v < a ? a : v > b ? b : v; }
    function prog(t, a, b) { return clamp((t - a) / (b - a), 0, 1); }
    function easeOutCubic(x) { return 1 - Math.pow(1 - x, 3); }
    function easeInOut(x) { return x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2; }
    function easeInOutSine(x) { return -(Math.cos(Math.PI * x) - 1) / 2; }

    /* Línea de tiempo (ms) */
    var T = { walk0: 250, walk1: 2650, coil: 2950, strike0: 2950, strike1: 3170, drop0: 3250, drop1: 3850, fade0: 3350, fade1: 4250, fin: 4400 };

    /* --- El alacrán de la entrada (misma anatomía y marcha) ---------- */
    var STRIDE = 14, CYCLE = STRIDE / 0.6, LIFT = 8;
    var HIPS = [17, 10, 2, -6], REACH = [30, 12, -10, -28], NEAR_OFF = [0.0, 0.5, 0.1, 0.6];
    var FEMUR = 24, TIBIA = 29;
    var gait = 0;

    function footAt(i, far) {
      var p = (gait + NEAR_OFF[i] + (far ? 0.5 : 0) + i * 0.04) % 1;
      if (p < 0) p += 1;
      var rest = HIPS[i] + REACH[i] * (far ? 0.86 : 1);
      if (p < 0.6) return { x: rest + STRIDE * (0.5 - p / 0.6), y: 0 };
      var w = (p - 0.6) / 0.4;
      return { x: rest + STRIDE * (-0.5 + w), y: -Math.sin(w * Math.PI) * LIFT };
    }
    function knee(hx, hy, fx, fy) {
      var dx = fx - hx, dy = fy - hy;
      var d = clamp(Math.sqrt(dx * dx + dy * dy), 1, FEMUR + TIBIA - 0.5);
      var th = Math.atan2(dy, dx);
      var a = Math.acos(clamp((FEMUR * FEMUR + d * d - TIBIA * TIBIA) / (2 * FEMUR * d), -1, 1));
      var k1 = { x: hx + FEMUR * Math.cos(th - a), y: hy + FEMUR * Math.sin(th - a) };
      var k2 = { x: hx + FEMUR * Math.cos(th + a), y: hy + FEMUR * Math.sin(th + a) };
      return k1.y < k2.y ? k1 : k2;
    }
    function geometry(t, pose) {
      var by = -19 + Math.sin(gait * Math.PI * 4) * 0.45 * pose.moving;
      var g = { by: by, legs: [], palps: [], tail: [], open: pose.claw };
      [true, false].forEach(function (far) {
        for (var i = 0; i < 4; i++) {
          var hx = HIPS[i] + (far ? 2 : 0), hy = by + (far ? 1 : 2.5);
          var f = footAt(i, far);
          g.legs.push({ far: far, h: { x: hx, y: hy }, k: knee(hx, hy, f.x, f.y), f: f });
        }
        var sway = Math.sin(t * 0.0023 + (far ? 1.4 : 0)) * 0.9;
        var dx = far ? 3 : 0, dy = far ? -2.5 : 0;
        g.palps.push({ far: far, a: { x: 33 + dx, y: by + 1 + dy }, b: { x: 44 + dx, y: by - 9 + dy + sway }, c: { x: 55 + dx, y: by - 6 + dy + sway }, rot: -0.12 + sway * 0.02 });
      });
      var tsway = Math.sin(t * 0.0019) * 0.035;
      var lens = [12, 12.5, 13, 13.5, 14], widths = [8, 7.4, 7, 6.6, 6.4];
      var x = -41, y = by - 4;
      var wind = pose.wind || 0, strike = clamp(pose.strike || 0, 0, 1.15);
      var a0 = Math.PI + 0.52 - pose.curl * 0.38 + tsway - wind * 0.22;
      var inc0 = 0.5 + pose.curl * 0.095 + tsway * 0.5 - wind * 0.1;
      var a = a0 + (4.5 - a0) * strike, inc = inc0 + (0.56 - inc0) * strike, reach = 1 + 0.38 * strike;
      for (var j = 0; j < lens.length; j++) {
        var nx = x + Math.cos(a) * lens[j] * reach, ny = y + Math.sin(a) * lens[j] * reach;
        g.tail.push({ x1: x, y1: y, x2: nx, y2: ny, w: widths[j], a: a });
        x = nx; y = ny; a += inc;
      }
      g.telson = { x: x, y: y, a: a - 0.2 };
      var ta = g.telson.a;
      g.tip = { x: x + Math.cos(ta) * 19.8 - Math.sin(ta) * 10, y: y + Math.sin(ta) * 19.8 + Math.cos(ta) * 10 };
      return g;
    }

    /* Silueta a contraluz: relleno de noche y un borde del resplandor */
    function style(mode, far) {
      if (mode === 'rim') return { c: far ? 'rgba(221,122,60,0.12)' : 'rgba(251,160,96,0.6)', g: 0.24 };
      return { c: far ? '#120b09' : '#0a0606', g: 0 };
    }
    function seg(x1, y1, x2, y2, w, st) {
      ctx.strokeStyle = st.c; ctx.lineWidth = w + st.g * 2;
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2); ctx.stroke();
    }
    function solid(draw, st) {
      ctx.fillStyle = st.c; ctx.strokeStyle = st.c; ctx.lineWidth = st.g * 2;
      ctx.beginPath(); draw(); ctx.closePath(); ctx.fill();
      if (st.g > 0) ctx.stroke();
    }
    function drawLegs(g, far, mode) {
      var st = style(mode, far), k = far ? 0.8 : 1;
      g.legs.forEach(function (Lg) {
        if (Lg.far !== far) return;
        seg(Lg.h.x, Lg.h.y, Lg.k.x, Lg.k.y, 2.1 * k, st);
        seg(Lg.k.x, Lg.k.y, Lg.f.x, Lg.f.y, 1.45 * k, st);
        seg(Lg.f.x, Lg.f.y, Lg.f.x + 3.2, Lg.f.y + 0.3, 0.9 * k, st);
      });
    }
    function drawPalp(g, far, mode) {
      var st = style(mode, far);
      g.palps.forEach(function (P) {
        if (P.far !== far) return;
        seg(P.a.x, P.a.y, P.b.x, P.b.y, 3.0, st);
        seg(P.b.x, P.b.y, P.c.x, P.c.y, 3.4, st);
        ctx.save(); ctx.translate(P.c.x + 9, P.c.y - 1); ctx.rotate(P.rot);
        solid(function () {
          ctx.moveTo(-9, 0);
          ctx.bezierCurveTo(-8, -6, 4, -7.2, 9, -3.4);
          ctx.bezierCurveTo(11, -1.6, 11, 1.6, 9, 3.2);
          ctx.bezierCurveTo(4, 6.4, -8, 5.6, -9, 0);
        }, st);
        seg(8, -2.6, 21, -3.6 - g.open * 4.5, 2.1, st);
        seg(8, 2.2, 20, 2.6 + g.open * 3.2, 1.9, st);
        ctx.restore();
      });
    }
    function drawTail(g, mode) {
      var st = style(mode, false);
      g.tail.forEach(function (s) { seg(s.x1, s.y1, s.x2, s.y2, s.w, st); });
      ctx.save(); ctx.translate(g.telson.x, g.telson.y); ctx.rotate(g.telson.a);
      solid(function () { ctx.ellipse(4.5, 0, 7.5, 5.2, 0, 0, Math.PI * 2); }, st);
      solid(function () { ctx.moveTo(10, -1.8); ctx.quadraticCurveTo(20.5, -1.2, 19.8, 10); ctx.quadraticCurveTo(17, 2.6, 10, 2); }, st);
      ctx.restore();
    }
    function drawBody(g, mode) {
      var st = style(mode, false), by = g.by;
      solid(function () {
        ctx.moveTo(36, by + 1);
        ctx.bezierCurveTo(36, by - 6, 30, by - 9, 20, by - 9.5);
        ctx.bezierCurveTo(8, by - 10, -8, by - 10.8, -24, by - 9.8);
        ctx.bezierCurveTo(-34, by - 9.2, -41, by - 6.5, -43, by - 2.5);
        ctx.bezierCurveTo(-43.5, by + 2.5, -36, by + 5.2, -26, by + 5.6);
        ctx.bezierCurveTo(-10, by + 6.5, 14, by + 6, 28, by + 5);
        ctx.bezierCurveTo(34, by + 4.4, 36, by + 3, 36, by + 1);
      }, st);
    }
    function pass(g, mode) {
      ctx.lineCap = 'round'; ctx.lineJoin = 'round';
      drawLegs(g, true, mode); drawPalp(g, true, mode); drawTail(g, mode);
      drawBody(g, mode); drawLegs(g, false, mode); drawPalp(g, false, mode);
    }
    function detail(g) {
      ctx.lineCap = 'round'; ctx.strokeStyle = 'rgba(251,170,110,0.08)'; ctx.lineWidth = 0.6;
      [13, 6, -1, -8, -15, -22, -29, -36].forEach(function (x) {
        ctx.beginPath(); ctx.moveTo(x + 1.6, g.by - 9.8); ctx.quadraticCurveTo(x + 3.2, g.by - 2, x - 0.6, g.by + 5.4); ctx.stroke();
      });
    }

    /* Luz del aguijón: núcleo, halo y destello */
    function light(x, y, p, r0) {
      if (p <= 0) return;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      var r = r0 * (0.6 + 0.6 * p);
      var g = ctx.createRadialGradient(x, y, 0, x, y, r);
      g.addColorStop(0, 'rgba(255,236,206,' + Math.min(1, p) + ')');
      g.addColorStop(0.2, 'rgba(251,141,69,' + 0.55 * p + ')');
      g.addColorStop(0.55, 'rgba(221,122,60,' + 0.16 * p + ')');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(255,226,190,' + 0.55 * Math.min(1, p) + ')';
      ctx.lineWidth = 0.8;
      ctx.beginPath(); ctx.moveTo(x - r * 1.8, y); ctx.lineTo(x + r * 1.8, y); ctx.moveTo(x, y - r * 0.9); ctx.lineTo(x, y + r * 0.9); ctx.stroke();
      ctx.restore();
    }

    /* Dónde detenerse para que el aguijón quede justo sobre el punto de la línea */
    var tipStrike = geometry(T.strike1, { moving: 0, curl: 1, wind: 0, strike: 1, claw: 1 }).tip;
    var lunge = 6;
    var dust = [];
    var prevX = null, t0 = null, born = null, ended = false;

    function frame(now) {
      if (ended) return;
      if (t0 === null) t0 = now;
      var t = now - t0;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, W, H);

      var stopX = L.cx - (tipStrike.x + lunge) * S;
      var startX = Math.min(L.x0 + 10 * S, stopX - 120 * S);
      var walkU = prog(t, T.walk0, T.walk1);
      var x = startX + (stopX - startX) * easeInOutSine(walkU);
      if (prevX === null) prevX = x;
      var dx = (x - prevX) / S; prevX = x;
      gait += dx / CYCLE;
      var coil = easeInOut(prog(t, T.walk1, T.coil));
      var sU = prog(t, T.strike0, T.strike1);
      var strike = sU <= 0 ? 0 : 1 - Math.pow(1 - sU, 4) + Math.sin(sU * Math.PI) * 0.12;
      var pose = { moving: clamp(dx * 2.2, 0, 1), curl: coil, wind: coil * (1 - Math.min(1, strike * 1.4)), strike: strike, claw: 0.35 + 0.65 * coil };
      var bx = x + lunge * strike * S;
      var alpha = Math.min(easeOutCubic(prog(t, T.walk0, T.walk0 + 500)), 1 - easeInOut(prog(t, T.fade0, T.fade1)));

      var g = null;
      if (alpha > 0) {
        ctx.save();
        ctx.globalAlpha = alpha;
        /* sombra sobre la línea */
        var sh = ctx.createRadialGradient(bx, L.y + 1, 0, bx, L.y + 1, 60 * S);
        sh.addColorStop(0, 'rgba(0,0,0,0.55)'); sh.addColorStop(1, 'rgba(0,0,0,0)');
        ctx.save(); ctx.translate(bx, L.y + 1); ctx.scale(1, 0.08); ctx.translate(-bx, -(L.y + 1));
        ctx.fillStyle = sh; ctx.beginPath(); ctx.arc(bx, L.y + 1, 60 * S, 0, Math.PI * 2); ctx.fill(); ctx.restore();
        ctx.translate(bx, L.y - 0.5); ctx.scale(S, S);
        g = geometry(t, pose);
        ctx.save(); ctx.translate(0.28, -0.42); pass(g, 'rim'); ctx.restore();
        pass(g, 'fill'); detail(g);
        ctx.restore();
      }
      /* polvo fino al apoyar y al atacar */
      if (pose.moving > 0.05 && Math.random() < 0.35) dust.push({ x: bx + (Math.random() * 60 - 30) * S, y: L.y, vx: Math.random() * 10 - 6, vy: -Math.random() * 10 - 3, life: 0, max: 700 + Math.random() * 400 });
      if (sU > 0 && sU < 0.25 && !frame.k) { frame.k = true; for (var k = 0; k < 18; k++) dust.push({ x: bx + (Math.random() * 70 - 25) * S, y: L.y, vx: Math.random() * 40 - 20, vy: -Math.random() * 26 - 6, life: 0, max: 800 + Math.random() * 500 }); }
      for (var i = dust.length - 1; i >= 0; i--) {
        var d = dust[i]; d.life += 16;
        if (d.life > d.max) { dust.splice(i, 1); continue; }
        d.x += d.vx * 0.016; d.y += d.vy * 0.016; d.vy += 0.35;
        if (d.y > L.y) d.y = L.y;
        ctx.fillStyle = 'rgba(221,160,110,' + 0.4 * (1 - d.life / d.max) + ')';
        ctx.fillRect(d.x - 0.7, d.y - 0.7, 1.4, 1.4);
      }

      /* La luz: nace en el aguijón y baja al punto de la línea */
      var ign = easeOutCubic(prog(t, T.strike0 + 120, T.strike1 + 80));
      if (ign > 0) {
        if (g && born === null && t >= T.strike1) born = { x: bx + g.tip.x * S, y: L.y - 0.5 + g.tip.y * S };
        var src = born || (g ? { x: bx + g.tip.x * S, y: L.y - 0.5 + g.tip.y * S } : { x: L.cx, y: L.y });
        var dU = easeInOut(prog(t, T.drop0, T.drop1));
        var lx = src.x + (L.cx - src.x) * dU, ly = src.y + (L.y - src.y) * dU;
        var settle = 1 - prog(t, T.drop1, T.fin) * 0.85;
        light(lx, ly, ign * settle * (0.92 + 0.08 * Math.sin(t * 0.012)), 46 * Math.min(1.2, S / 1.6 + 0.3));
      }
      if (t >= T.drop1 && !frame.on) { frame.on = true; terminar(null); }
      if (t >= T.fin + 200) { ended = true; canvas.classList.add('is-apagada'); window.removeEventListener('resize', medir); ctx.clearRect(0, 0, W, H); return; }
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', iniciar);
  else iniciar();
})();
